import { CustomHandle } from '@/adapter/blockAdapter';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { useThemeMode } from '@/hooks/useThemeMode';
import { BackendNode } from '@/types/backendNode';
import { Detail } from '@/types/detail';
import { findSubroutine } from '@/util/application';
import { isConnection, isNumber, isString } from '@/util/typeGuards';
import { NodeProps, useReactFlow } from '@xyflow/react';
import { FC, memo, useMemo } from 'react';
import CustomNode from './nodes/CustomNode';
import CustomNodeAlt from './nodes/CustomNodeAlt';
import NodeSelection from './nodes/NodeSelection';
import NodeValidator from './nodes/NodeValidator';

type NodePropsWithoutData = Omit<NodeProps, 'data'>;

export type CustomNodeProps = NodePropsWithoutData & {
  id: string;
  data: CustomNodeData;
  size?: { width: string; height: string };
};

export type ExtendedCustomNodeProps = CustomNodeProps & {
  blockStyle: string | undefined;
  isStartNode: boolean;
  sourceHandles: CustomHandle[];
  label?: string;
};

export type CustomNodeData = {
  backendNode: BackendNode;
  openDetails?: boolean;
  onDetailsClicked?: () => void;
  details?: Detail;
  handles?: CustomHandle[];
};

// A source handle id is `${node.id}.source.${connection.id}`, so the connection id is the last segment.
const connectionIdOf = (handle: CustomHandle) => handle.id.substring(handle.id.lastIndexOf('.') + 1);

const NodeRenderer: FC<CustomNodeProps> = (props) => {
  const { metadata } = useMetadata();
  const { flowTheme } = useThemeMode();
  const { application } = useApplication();
  const { getZoom } = useReactFlow();

  const zoom = getZoom();
  const connections = application.blocks.connections[props.id];

  const blockStyle = useMemo(() => {
    return metadata.blocks.find((block) => block.type === props.data.backendNode.type)?.style;
  }, [metadata.blocks, props.data.backendNode.type]);

  const sourceHandles = useMemo(() => {
    const handles = props.data.handles?.filter((handle) => handle.type === 'source') || [];

    // Updating the labels of sourceHandles based on connections
    return handles.map((handle) => {
      const connectionEntry = connections?.find(
        (connection) => isConnection(connection) && connection.id?.toString() === connectionIdOf(handle)
      );

      return connectionEntry ? { ...handle, label: connectionEntry.label || connectionEntry.action } : handle;
    });
  }, [props.data.handles, connections]);

  const getNodeLabel = () => {
    if (!application.blocks.nodes[props.id]) return;
    if (application.blocks.nodes[props.id].label) {
      return application.blocks.nodes[props.id].label;
    }

    if (application.blocks.nodes[props.id].type === 'Gosub' || application.blocks.nodes[props.id].type === 'RunTask') {
      const subroutineId = application.blocks.details[props.id].substart;
      return isNumber(subroutineId) ? findSubroutine(application.subroutines, subroutineId)?.label : undefined;
    }
    if (application.blocks.nodes[props.id].type === 'DBExecute') {
      const spName = application.blocks.details[props.id]['sp_name'];
      if (!isString(spName)) return;
      const storedProcedure = spName.split('.');
      return storedProcedure[storedProcedure.length - 1];
    }
  };

  return (
    <NodeValidator nodeId={parseInt(props.id)}>
      <NodeSelection {...props} zoom={zoom || 1}>
        {flowTheme === 'alt' ? (
          <CustomNodeAlt
            {...props}
            label={getNodeLabel()}
            blockStyle={blockStyle}
            isStartNode={blockStyle === 'appstart' || blockStyle === 'subroutine'}
            sourceHandles={sourceHandles}
          />
        ) : (
          <CustomNode
            {...props}
            label={getNodeLabel()}
            blockStyle={blockStyle}
            isStartNode={blockStyle === 'appstart' || blockStyle === 'subroutine'}
            sourceHandles={sourceHandles}
          />
        )}
      </NodeSelection>
    </NodeValidator>
  );
};

const MemoizedNodeRenderer = memo(NodeRenderer, (prev, next) => {
  return prev.id === next.id && prev.data === next.data && prev.selected === next.selected;
});

export default MemoizedNodeRenderer;
